import { ChatMessageAuthorType } from 'enums/modules/chat/chatMessageAuthorType';
import { ChatMessageStatus } from 'enums/modules/chat/chatMessageStatus';
import { ChatMessage, ChatMessageList } from 'model/modules/chat/chatMessage';
import mapChatListFromApi from 'rtkQuery/sharespro/mapper/modules/chat/mapChatListFromApi';
import mapChatMessagePaginationListFromApi, {
  mapChatMessageListFromApi,
  mapChatMessageListItemFromApi,
} from 'rtkQuery/sharespro/mapper/modules/chat/mapChatMessageListFromApi';
import sharesproApi from 'rtkQuery/sharespro/sharesproApiService';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import mergeChatList, { uniqueChatItems } from 'rtkQuery/utils/mergeChatList';
import paginationMergeResponse from 'rtkQuery/utils/paginationMergeResponse';
import { v4 as uuidv4 } from 'uuid';
import { ChatList, ChatDetailModel } from 'model/modules/chat/chatList';
import { AiChat, AiMessage, ApiAiChatResponse, ApiAiMessageResponse } from 'model/modules/chatAi/chatAi';
import mapAiMessageListFromApi, { mapAiChatFromApi } from 'rtkQuery/sharespro/mapper/modules/chat/mapAiMessageFromApi';

export const chatApi = sharesproApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchChatList: builder.query<
      ChatList,
      ApiPaginationQueryParams
    >({
      keepUnusedDataFor: 60 * 5,
      query: (params) => ({
        url: '/v5/client/chat/list',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiChatList>
      ) {
        const result = mapChatListFromApi(baseQueryReturnValue);
        result.chats.items = uniqueChatItems(result.chats.items);

        return result;
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: mergeChatList,
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
    }),
    fetchMessageList: builder.query<
      ChatMessageList,
      ApiChatMessageListRequestParams
    >({
      query: (params) => ({
        url: '/v3/client/chat/messages',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiPaginationListResponse<ApiChatMessage>
        >,
        meta,
        arg
      ) {
        return mapChatMessagePaginationListFromApi(
          baseQueryReturnValue,
          arg.chat_id
        );
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return { chat_id: queryArgs.chat_id };
      },
      merge: paginationMergeResponse,
      providesTags: (result) => {
        return [
          {
            type: 'ChatMessages',
            id: result?.chatId,
          },
        ];
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.page !== previousArg?.page ||
          currentArg?.chat_id !== previousArg?.chat_id
        );
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        if (data.page === 1) {
          try {
            await queryFulfilled;
            dispatch(chatApi.endpoints.readMessages.initiate(data.chat_id));
            dispatch(
              chatApi.util.updateQueryData(
                'fetchChatList',
                { page: 1 },
                (draft) => {
                  let prevUnreadMessagesCount = 0;
                  draft.chats.items = draft.chats.items.map<ChatDetailModel>((item) => {
                    if (item.id === data.chat_id) {
                      prevUnreadMessagesCount = item.unreadMessageCount;

                      return {
                        ...item,
                        unreadMessagesCount: 0,
                      };
                    } else {
                      return item;
                    }
                  });
                  draft.totalUnreadMessages = Math.max(draft.totalUnreadMessages - prevUnreadMessagesCount, 0);
                }
              )
            );
          } catch {}
        }
      },
    }),
    sendMessage: builder.mutation<ChatMessage, ApiChatSendMessageRequest>({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      query: ({ chat_id, ...data }) => {
        return {
          url: '/v3/client/chat/save',
          method: 'POST',
          sendToken: true,
          params: {
            chat_id,
          },
          body: {
            message: data.message,
            files: data.files?.map((file) => ({
              upload_id: file.id,
            })),
          },
        };
      },
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiChatMessage>
      ) {
        return mapChatMessageListItemFromApi(baseQueryReturnValue.data);
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const sendingId = uuidv4();
        try {
          dispatch(
            chatApi.util.updateQueryData(
              'fetchMessageList',
              { chat_id: data.chat_id, page: 1 },
              (draft) => {
                draft.items.unshift({
                  sendingId,
                  authorData: { isMe: true },
                  chatId: data.chat_id ?? -1,
                  authorType: ChatMessageAuthorType.Investor,
                  text: data.message ?? null,
                  createdAt: new Date(),
                  files: data.files?.map(value => { return { name: value.name, size: value.size, url: value.url, previewUrl: null, fileName: null, height: null, previewHeight: null, previewWidth: null, width: null, typeId: null, objectId: null, extension: null }; }) ?? [],
                  status: ChatMessageStatus.Sending,
                  updated: false,
                });
              }
            )
          );
          const resp = await queryFulfilled;
          dispatch(
            chatApi.util.updateQueryData(
              'fetchMessageList',
              { chat_id: data.chat_id, page: 1 },
              (draft) => {
                draft.items = draft.items.map((item) =>
                  item.sendingId === sendingId
                    ? {
                        ...item,
                      authorData: { isMe: true },
                        status: ChatMessageStatus.Sent,
                        id: resp.data.id,
                      }
                    : item
                );
              }
            )
          );
          dispatch(
            chatApi.util.updateQueryData(
              'fetchChatList',
              { page: 1 },
              (draft) => {
                // @ts-expect-error
                draft.chats.items = draft.chats.items.map<ChatDetailModel>((item) => {
                  return item.id === data.chat_id
                    ? {
                        ...item,
                        lastMessage: {
                          authorType: ChatMessageAuthorType.Investor,
                          text: data.message ?? null,
                          createdAt: new Date(),
                          files: resp.data.files,
                          updated: false,
                        },
                      }
                    : item;
                });
              }
            )
          );
        } catch {
          dispatch(
            chatApi.util.updateQueryData(
              'fetchMessageList',
              { chat_id: data.chat_id, page: 1 },
              (draft) => {
                draft.items = draft.items.map((item) =>
                  item.sendingId === sendingId

                    ? { ...item, status: ChatMessageStatus.Failed, authorData: { isMe: true } }
                    : { ...item, authorData: { isMe: true } }
                );
              }
            )
          );
        }
      },
    }),
    readMessages: builder.mutation<SharesproApiResponse<boolean>, ID>({
      query: (chatId) => ({
        url: '/v3/client/chat/read',
        method: 'POST',
        sendToken: true,
        params: {
          chat_id: chatId,
        },
      }),
    }),
    checkChatUpdates: builder.query<
      RemoteList<ChatMessage>,
      ApiChatCheckUpdatesRequestParams
    >({
      query: (params) => ({
        url: '/v3/client/chat/updates',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiListResponse<ApiChatMessage>
        >
      ) {
        return mapChatMessageListFromApi(baseQueryReturnValue);
      },
      async onQueryStarted(data, { queryFulfilled, dispatch }) {
        const resp = await queryFulfilled;
        if (resp.data.items.length > 0) {
          dispatch(
            chatApi.util.updateQueryData(
              'fetchMessageList',
              { chat_id: data.chat_id, page: 1 },
              (draft) => {
                draft.items = [...resp.data.items, ...draft.items];
              }
            )
          );
          const lastMessage = resp.data.items[0];
          dispatch(
            chatApi.util.updateQueryData(
              'fetchChatList',
              { page: 1 },
              (draft) => {
                // @ts-expect-error
                draft.chats.items = draft.chats.items.map<ChatDetailModel>((item) => {
                  return item.id === data.chat_id
                    ? {
                        ...item,
                        lastMessage: {
                          authorType: lastMessage.authorType,
                          managerName: lastMessage.managerName ?? null,
                          text: lastMessage.text,
                          createdAt: lastMessage.createdAt,
                          files: lastMessage.files,
                        },
                      }
                    : item;
                });
              }
            )
          );
        }
      },

    }),
     fetchAiChatList: builder.query<AiChat[], void>({
      query: () => ({
        url: '/v3/client/ai-chat/list',
        method: 'GET',
        sendToken: true,
      }),
      transformResponse(baseQueryReturnValue: SharesproApiResponse<{ items: ApiAiChatResponse[] }>) {
        return baseQueryReturnValue.data.items.map(mapAiChatFromApi);
      },
    }),
      fetchAiMessageList: builder.query<
      PaginationList<AiMessage>,
      { externalId: string; page: number }
    >({
      query: ({ externalId, page }) => ({
        url: '/v3/client/ai-chat/messages',
        method: 'GET',
        sendToken: true,
        params: {
          external_id: externalId,
          page,
        },
      }),
      transformResponse(baseQueryReturnValue: SharesproApiResponse< ApiPaginationListResponse<ApiAiMessageResponse>>) {
        return mapAiMessageListFromApi(baseQueryReturnValue);
      },
    }),
       sendAiMessage: builder.mutation<void, { externalId: string; messages: AiMessage[] }>({
      query: ({ externalId, messages }) => ({
        url: '/v3/client/ai-chat/push',
        method: 'POST',
        sendToken: true,
        params: { external_id: externalId },
        body: {
          messages: messages.map((m) => ({
            external_id: m.id,
            create_timestamp: Math.floor(m.createdAt.getTime() / 1000),
            text: m.text,
            ai: m.isFromAi,
            attachment: m.attachment,
          })),
        },
      }),
    }),
bindAiChat: builder.mutation<void, { externalId: string }>({
      query: ({ externalId }) => ({
        url: '/v3/client/ai-chat/bind',
        method: 'POST',
        sendToken: true,
        params: { external_id: externalId },
      }),
    }),
    removeAiChat: builder.mutation<void, { externalId: string }>({
      query: ({ externalId }) => ({
        url: '/v3/client/ai-chat/remove-chat',
        method: 'DELETE',
        sendToken: true,
        params: { external_id: externalId },
      }),
    }),
  }),
});

export const {
  useFetchChatListQuery,
  useFetchMessageListQuery,
  useSendMessageMutation,
  useLazyCheckChatUpdatesQuery,
  useReadMessagesMutation,
  useFetchAiChatListQuery,
  useFetchAiMessageListQuery,
  useSendAiMessageMutation,
  useBindAiChatMutation,
  useRemoveAiChatMutation,
} = chatApi;
